/* ---------------------------------------------------------------------------
   The defect vocabulary, shared between the register and the modeller.

   These codes, severities and statuses are a hand-copy of `DEFECT_TYPES`,
   `SEVERITIES` and `STATUSES` in
   `vendor/bridge-modeler/src/views/DefectFormChrome.ts`. Copied rather than
   imported because importing the real module pulls Three.js and the whole
   dashboard into the register's 145 kB bundle. `npm test` scans the vendored source and fails
   if the two have drifted — see scripts/modeler-defects.ts.

   **Why share a vocabulary at all.** A register defect can reference a defect in
   a model's own log (`modelDefectId`), and the two have to be talking about the
   same thing for that reference to mean anything. An inspector who logs
   "Scouring, Severe" at a pier in the modeller and an engineer who logs the same
   finding against a structure with no model should produce records that count
   together on the overview. One vocabulary is what makes that true.

   The modeller keeps writing its own log and is not edited. This is the
   register's side of the same language.
   --------------------------------------------------------------------------- */

export interface DefectCode {
  /** Three-letter category code, as the modeller writes it. */
  code: string;
  label: string;
}

/** Every defect category the modeller offers, in its own order. */
export const DEFECT_CODES: readonly DefectCode[] = [
  { code: 'SCA', label: 'Scaling' },
  { code: 'SPA', label: 'Spalling' },
  { code: 'REE', label: 'Exposed Rebar' },
  { code: 'POH', label: 'Pothole' },
  { code: 'SEP', label: 'Joint Separation' },
  { code: 'SCO', label: 'Scouring' },
  { code: 'SET', label: 'Settlement' },
  { code: 'CRK', label: 'Cracking' },
  { code: 'ERO', label: 'Erosion' },
];

/**
 * Severities, least serious first — the modeller's own order.
 *
 * `SEVERITIES` in src/data/derive.ts is the same four the other way round,
 * because a summary reads worst-first while a picker reads mildest-first. Both
 * are checked against this list.
 */
export const DEFECT_SEVERITIES = ['Minor', 'Moderate', 'Severe', 'Critical'] as const;

export type DefectSeverity = (typeof DEFECT_SEVERITIES)[number];

/**
 * The statuses the modeller knows — its own `STATUSES`, which the drift check
 * compares. The modeller's picker offers these and no others.
 */
export const MODELER_DEFECT_STATUSES = ['Open', 'In Review', 'Resolved'] as const;

/**
 * Where a defect has got to, in order (LC-62). A register defect starts Open.
 * After Resolved, the repair: Repaired, recorded by an engineer with a date and
 * a reference, then Verified by somebody who did not record the repair. Moves
 * go forward only; going back is a reopen, to Open, with a reason.
 */
export const DEFECT_STATUSES = ['Open', 'In Review', 'Resolved', 'Repaired', 'Verified'] as const;

export type DefectStatus = (typeof DEFECT_STATUSES)[number];

/**
 * The statuses a defect is no longer open in. Resolved was the only one before
 * the repair statuses; a repaired or verified defect is not open either, so
 * counts, the remedial estimate and a rating's damage inputs all leave it out.
 */
export const CLOSED_DEFECT_STATUSES: readonly string[] = ['Resolved', 'Repaired', 'Verified'];

/** Whether a defect in this status is still open. An unknown status is open. */
export function isOpenStatus(status: string): boolean {
  return !CLOSED_DEFECT_STATUSES.includes(status);
}

/** A status's place in the order, or -1 for one the register does not know. */
export function statusRank(status: string): number {
  return (DEFECT_STATUSES as readonly string[]).indexOf(status);
}

/**
 * Whether a defect may move from one status to another: forward, with Verified
 * only after Repaired, or back to Open as a reopen. Where it already is is no
 * move at all.
 */
export function defectMove(from: string, to: string): 'forward' | 'reopen' | null {
  const a = statusRank(from);
  const b = statusRank(to);
  if (a < 0 || b < 0 || a === b) return null;
  if (b > a) return to === 'Verified' && from !== 'Repaired' ? null : 'forward';
  return to === 'Open' ? 'reopen' : null;
}

/** One step of a defect's status history, as the register serves it compactly. */
export interface DefectStep {
  /** The day it took effect: the repair's own date for Repaired, else the day it was recorded. */
  on: string;
  status: string;
}

/**
 * Where a defect stood at the end of a day, from its steps; null where it had
 * not yet been found. With no steps held — a demo record, or a list from a
 * build before the history — the current status is all there is to say.
 */
export function statusOn(
  d: { status: string; loggedAt: string; timeline?: readonly DefectStep[] },
  date: string,
): string | null {
  if (d.loggedAt && d.loggedAt.slice(0, 10) > date) return null;
  const steps = d.timeline ?? [];
  if (!steps.length) return d.status;
  let at: string = steps[0]!.status;
  for (const step of steps) if (step.on <= date) at = step.status;
  return at;
}

const CODES = new Map(DEFECT_CODES.map((d) => [d.code, d]));

/** Label for a stored code, falling back to the code itself. */
export function defectCodeLabel(code: string): string {
  return CODES.get(code)?.label ?? code;
}

export function isKnownDefectCode(code: string): boolean {
  return CODES.has(code);
}

export function isDefectSeverity(v: string): v is DefectSeverity {
  return (DEFECT_SEVERITIES as readonly string[]).includes(v);
}

export function isDefectStatus(v: string): v is DefectStatus {
  return (DEFECT_STATUSES as readonly string[]).includes(v);
}

/**
 * How many photographs the register holds for a defect.
 *
 * A row without `photoCount` predates defects carrying several, and had one
 * exactly when it said `hasPhoto` — see the note on `RegisterDefect.photoCount`.
 */
export function defectPhotoCount(d: { hasPhoto: boolean; photoCount?: number }): number {
  if (!d.hasPhoto) return 0;
  return d.photoCount ?? 1;
}

/* ---------------------------------------------------------------------------
   The modeller's own record shape — the wire between this store and the
   vendored Defects inspector.

   `DefectRecord` in vendor/bridge-modeler/src/views/DefectsView.ts, as an array
   under the `bim_modeler_defects` localStorage key. Until migration 0015 that
   key WAS the store for model-pinned defects. It is now a projection: the shim
   in src/modeler-host/boot.ts seeds it from the `defects` table on boot and
   hands the array back on every save, and functions/lib/defects.ts reconciles it
   into rows.

   The vendored modeller is not edited and does not know. It still calls
   `localStorage.getItem` and `setItem`, which is the whole reason this shape has
   to be honoured exactly rather than improved on.
   --------------------------------------------------------------------------- */

/**
 * One entry in the array the modeller reads and writes.
 *
 * The legacy mirror fields (`defect_code`, `world_x`, `created_at`, …) that the
 * older DefectPanel also reads are written by `toModelerRecord` but are not part
 * of this interface: nothing in this codebase reads them, and declaring them
 * would invite something to start.
 */
export interface ModelerDefectRecord {
  /** The integer handle. Positive for a record that has reached this backend. */
  id: number;
  component: string;
  code: string;
  label: string;
  severity: string;
  status: string;
  notes: string;
  loggedAt: string;
  /**
   * The photograph, or null.
   *
   * A URL on the way out — `/api/modeler/defect-photo?…` — which the modeller
   * assigns straight to `img.src` and to a `new Image()` for the surface decal,
   * so it works as well as a data URL and costs a few dozen bytes instead of a
   * few hundred kilobytes on every save. A `data:` URL on the way IN is a
   * photograph the inspector has just attached, and the only thing that causes an
   * upload.
   */
  image: string | null;
  /** Pin anchor, metres. Zero on all three axes is the modeller's own "not picked from 3D". */
  worldX: number;
  worldY: number;
  worldZ: number;
  /** Surface normal at the pin. Absent, not zero, when there is no surface. */
  worldNormalX?: number;
  worldNormalY?: number;
  worldNormalZ?: number;
}

/** A number from an untrusted record, or null for anything that is not finite. */
function finite(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Read an array of the modeller's records out of untrusted JSON.
 *
 * Tolerant by design, and in exactly the way the vendored `LoadDefects` is: this
 * parses a value the modeller wrote, on a shape neither side owns, so an entry
 * missing a field is dropped rather than failing the whole array. One unreadable
 * record must not cost an inspector the other twenty.
 *
 * Both field spellings are accepted for the same reason the vendored loader
 * accepts both — the older DefectPanel wrote `defect_code`/`created_at`/`world_x`
 * and there are logs in R2 carrying them.
 */
export function readModelerLog(json: string): ModelerDefectRecord[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out: ModelerDefectRecord[] = [];
  for (const raw of parsed) {
    if (typeof raw !== 'object' || raw === null) continue;
    const r = raw as Record<string, unknown>;

    const id = finite(r['id']);
    const component = typeof r['component'] === 'string' ? r['component'] : null;
    const code = typeof r['code'] === 'string' ? r['code'] : typeof r['defect_code'] === 'string' ? r['defect_code'] : null;
    if (id === null || !Number.isInteger(id) || component === null || code === null) continue;

    const severity = typeof r['severity'] === 'string' ? r['severity'] : 'Minor';
    const status =
      typeof r['status'] === 'string'
        ? r['status']
        : typeof r['description'] === 'string'
          ? r['description']
          : 'Open';

    out.push({
      id,
      component,
      code,
      label: typeof r['label'] === 'string' ? r['label'] : defectCodeLabel(code),
      severity,
      status,
      notes:
        typeof r['notes'] === 'string'
          ? r['notes']
          : typeof r['field_notes'] === 'string'
            ? r['field_notes']
            : '',
      loggedAt:
        typeof r['loggedAt'] === 'string'
          ? r['loggedAt']
          : typeof r['created_at'] === 'string'
            ? r['created_at']
            : '',
      image: typeof r['image'] === 'string' && r['image'] !== '' ? r['image'] : null,
      worldX: finite(r['worldX']) ?? finite(r['world_x']) ?? 0,
      worldY: finite(r['worldY']) ?? finite(r['world_y']) ?? 0,
      worldZ: finite(r['worldZ']) ?? finite(r['world_z']) ?? 0,
      ...normalOf(r),
    });
  }
  return out;
}

/** The three normal components, present together or not at all. */
function normalOf(r: Record<string, unknown>): Partial<ModelerDefectRecord> {
  const x = finite(r['worldNormalX']) ?? finite(r['world_normal_x']);
  const y = finite(r['worldNormalY']) ?? finite(r['world_normal_y']);
  const z = finite(r['worldNormalZ']) ?? finite(r['world_normal_z']);
  /* All or nothing. A normal with one axis missing is not a direction, and
     defaulting the missing one to zero would lay a decal at an angle nobody
     measured. */
  if (x === null || y === null || z === null) return {};
  return { worldNormalX: x, worldNormalY: y, worldNormalZ: z };
}

/**
 * The modeller's own "not picked from 3D" sentinel.
 *
 * It writes `worldX/Y/Z: 0` for a defect added from the component table and
 * falls back to the component's bbox centre when drawing it. Exact zero on all
 * three axes is therefore a flag rather than a coordinate — the origin of a
 * bridge model is a point on the alignment, not on a girder, so nothing real
 * lands there.
 */
export function isUnpinned(r: ModelerDefectRecord): boolean {
  return r.worldX === 0 && r.worldY === 0 && r.worldZ === 0;
}
